/**
 * Helpers for `<input type="date">` values, which are always plain
 * `YYYY-MM-DD` strings interpreted in the user's local timezone.
 *
 * Everything here works in local time on purpose: `toISOString().slice(0, 10)`
 * shifts the day for anyone west of UTC, which would show an organizer a date
 * they never picked.
 */

/** `YYYY-MM-DD` for a Date, using local calendar fields. */
function formatLocal(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Coerce an ISO datetime (or plain date) into the `YYYY-MM-DD` shape an
 * `<input type="date">` expects. Anything we can't parse is swallowed — we
 * don't want a stray API value to blow up the form.
 */
export function toDateInputValue(value: string | undefined | null): string | null {
  if (!value) return null;
  // If already in YYYY-MM-DD, keep as-is.
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return formatLocal(d);
}

/** Today in the user's timezone, as a date-input value. */
export function todayInputValue(): string {
  return formatLocal(new Date());
}

/**
 * Shift a date-input value by whole days. Returns the input unchanged when it
 * isn't a value we can parse.
 */
export function addDaysToInputValue(base: string, days: number): string {
  const normalized = toDateInputValue(base);
  if (!normalized) return base;
  const [y, m, d] = normalized.split('-').map(Number) as [number, number, number];
  // Local midnight + N days; Date handles month/year rollover and DST for us.
  const shifted = new Date(y, m - 1, d + days);
  return formatLocal(shifted);
}

/**
 * True when a date-input value falls before today. Today itself is not past —
 * ad platforms accept a campaign starting today.
 */
export function isPastInputValue(value: string | undefined | null): boolean {
  const normalized = toDateInputValue(value);
  if (!normalized) return false;
  // Both sides are zero-padded YYYY-MM-DD, so lexical order is date order.
  return normalized < todayInputValue();
}
